import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'
import { saveReferenceCache, loadReferenceCache, clearReferenceCache } from './offlineStore'
import { DIFFICULTY_LEVELS } from './archConfig'

/**
 * useArchCivilDifficultyLevels — loads civil difficulty/complexity levels from
 * `arch_civil_difficulty_options`.
 * Falls back to the hardcoded DIFFICULTY_LEVELS array (same as new construction)
 * if the table doesn't exist or returns empty.
 */
export function useArchCivilDifficultyLevels() {
    const [levels,  setLevels]  = useState(DIFFICULTY_LEVELS)
    const [loading, setLoading] = useState(true)
    const [error,   setError]   = useState(null)

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        const cached = await loadReferenceCache('arch_civil_difficulty_options', null).catch(() => null)
        if (cached && cached.data && cached.data.length > 0) {
            setLevels(cached.data.map(r => ({
                id:    r.id,
                value: Number(r.value),
                label: r.label,
                desc:  r.description,
            })))
            setLoading(false)
        }
        const { data, error: err } = await supabase
            .from('arch_civil_difficulty_options')
            .select('*')
            .order('sort_order', { ascending: true })
        if (err) {
            console.error('[archCivilDifficulty] fetch error:', err.message)
            setError(err.message)
            // keep the static fallback already in state
        } else {
            if (data && data.length > 0) {
                setLevels(data.map(r => ({
                    id:    r.id,
                    value: Number(r.value),
                    label: r.label,
                    desc:  r.description,
                })))
            }
            await saveReferenceCache('arch_civil_difficulty_options', data || [], null).catch(() => {})
        }
        setLoading(false)
    }, [])

    useEffect(() => { load() }, [load])

    const upsertLevel = useCallback(async ({ id, value, label, desc, sort_order }) => {
        const payload = {
            value:       Number(value),
            label:       label.trim(),
            description: (desc || '').trim(),
            sort_order:  Number(sort_order) || 0,
            updated_at:  new Date().toISOString(),
        }
        if (id) {
            const { data, error: err } = await supabase
                .from('arch_civil_difficulty_options')
                .update(payload)
                .eq('id', id)
                .select()
                .single()
            if (err) throw new Error(err.message)
            const mapped = { id: data.id, value: Number(data.value), label: data.label, desc: data.description }
            setLevels(prev => prev.map(l => l.id === id ? mapped : l))
            await clearReferenceCache('arch_civil_difficulty_options', null).catch(() => {})
            return mapped
        }
        const { data, error: err } = await supabase
            .from('arch_civil_difficulty_options')
            .insert(payload)
            .select()
            .single()
        if (err) throw new Error(err.message)
        const mapped = { id: data.id, value: Number(data.value), label: data.label, desc: data.description }
        setLevels(prev => {
            const next = [...prev, mapped]
            return next.sort((a, b) => a.value - b.value)
        })
        await clearReferenceCache('arch_civil_difficulty_options', null).catch(() => {})
        return mapped
    }, [])

    const removeLevel = useCallback(async (id) => {
        setLevels(prev => {
            if (prev.length <= 1) throw new Error('Must keep at least one difficulty level')
            return prev
        })
        const { error: err } = await supabase
            .from('arch_civil_difficulty_options')
            .delete()
            .eq('id', id)
        if (err) throw new Error(err.message)
        setLevels(prev => prev.filter(l => l.id !== id))
        await clearReferenceCache('arch_civil_difficulty_options', null).catch(() => {})
    }, [])

    return { levels, loading, error, reload: load, upsertLevel, removeLevel }
}
